import * as React from 'react';
import { AppRouteComponentMatchParams, AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import styles from '../styles/settingsStyles';
import Descriptions from 'antd/lib/descriptions';
import {
    displayTextForLifecycleFreezeSets,
    displayTextForDayOfTheWeek,
    displayTextForReleaseScheduleCadenceStyle,
} from '../../helpers/displayHelpers';
import { runwayColors, tagStyle } from '../styles/globalStyles';
import { AppStoreType, DayOfTheWeek, LifecycleStep, ReleaseScheduleCadenceStyle, SettingType } from '../../types';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import { selectAppStoreType, selectLifecycleFreezes, selectScheduleSettings } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { useHistory, useParams } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { dayjs } from '@/extendedDayjs';
import Button from 'antd/lib/button';
import { getTargetsConfiguredForStepTypeFromDateSet, isMultipleReleasesCadence } from '@/helpers';
import { formatDateWithTimezone, getTimezoneDiff } from '@/timeHelpers';

const ReleaseScheduleSettingsComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const scheduleSettings = useSelector((state: RootState) => selectScheduleSettings(state, props.appId));
    const lifecycleFreezes = useSelector((state: RootState) => selectLifecycleFreezes(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isAppleApp = appStoreType == AppStoreType.Apple;

    const haveMultiReleaseCadence = scheduleSettings ? isMultipleReleasesCadence(scheduleSettings) : false;

    const history = useHistory();
    const { appIdParam, orgIdParam } = useParams<AppRouteComponentMatchParams>();

    const scheduleIsActive =
        scheduleSettings ? scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never : false;

    const onClickGoToReleaseSettings = () => {
        const defaultsRoute = createAppSettingsRouteWithSettingType(
            orgIdParam,
            appIdParam,
            SettingType.ScheduleCadence,
        );

        history.push(defaultsRoute);
    };

    const dateComponent = (weekday: DayOfTheWeek, hour: number, minute: number, timezone: string) => {
        const momentTime = minute ? dayjs.utc(`${hour}:${minute}`, 'HH:mm') : dayjs.utc(`${hour}`, 'HH');
        return (
            <>
                {displayTextForDayOfTheWeek(weekday)} at{' '}
                {formatDateWithTimezone(momentTime, 'hourOnly', timezone, false)} {getTimezoneDiff(timezone, 'GMT')}
            </>
        );
    };

    const renderConfiguredDateInfosOfType = (stepType: LifecycleStep) => {
        if (!scheduleSettings.cadenceTargetDateSets) {
            return <Text type="secondary">Not set</Text>;
        }
        const timezone = scheduleSettings?.cadenceTimezone;

        if (haveMultiReleaseCadence) {
            const dateComponents: React.JSX.Element[] = [];

            for (const dateSet of scheduleSettings.cadenceTargetDateSets) {
                const configuredTargets = getTargetsConfiguredForStepTypeFromDateSet(dateSet, stepType);

                if (configuredTargets) {
                    dateComponents.push(
                        dateComponent(configuredTargets[0], configuredTargets[1], configuredTargets[2], timezone),
                    );
                }
            }

            if (dateComponents.length === 0) {
                return <Text type="secondary">Not set</Text>;
            } else {
                return dateComponents.map((component, i) => {
                    return (
                        <li key={i}>
                            <span>• {component}</span>
                        </li>
                    );
                });
            }
        } else if (scheduleSettings?.cadenceTargetDateSets?.length > 0) {
            const dateSet = scheduleSettings.cadenceTargetDateSets[0];
            const configuredTargets = getTargetsConfiguredForStepTypeFromDateSet(dateSet, stepType);
            if (configuredTargets) {
                return dateComponent(configuredTargets[0], configuredTargets[1], configuredTargets[2], timezone);
            } else {
                return <Text type="secondary">Not set</Text>;
            }
        } else {
            return <Text type="secondary">Not set</Text>;
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Space>
                        <Text style={styles.appSettingsSectionTitleStyle}>Release schedule</Text>
                        <Tag
                            style={tagStyle}
                            color={scheduleIsActive ? runwayColors.statusGreen : runwayColors.statusYellow}
                        >
                            {scheduleIsActive ? 'Active' : 'Inactive'}
                        </Tag>
                    </Space>
                    <Button onClick={onClickGoToReleaseSettings}>Schedule settings</Button>
                </div>
                <Text type="secondary">
                    Set default target dates for kickoff, submission, and release on a regular cadence. If respective
                    release cycle automations are enabled, they will execute based on target dates set in the release
                    schedule.
                </Text>
                <Descriptions
                    bordered
                    column={1}
                    style={{ marginTop: 24, marginBottom: 12 }}
                    labelStyle={{ width: 240 }}
                >
                    <Descriptions.Item label={'Release schedule cadence'}>
                        {scheduleSettings?.scheduleCadence ?
                            displayTextForReleaseScheduleCadenceStyle(
                                scheduleSettings.scheduleCadence,
                                haveMultiReleaseCadence,
                            )
                        :   <Text type="secondary">Not enabled</Text>}
                    </Descriptions.Item>
                    {scheduleIsActive && (
                        <Descriptions.Item label="Kickoff date, time">
                            {renderConfiguredDateInfosOfType(LifecycleStep.Kickoff)}
                        </Descriptions.Item>
                    )}
                    {scheduleIsActive && (
                        <Descriptions.Item label="Submit date, time">
                            {renderConfiguredDateInfosOfType(LifecycleStep.Submission)}
                        </Descriptions.Item>
                    )}
                    {scheduleIsActive && isAppleApp && (
                        <Descriptions.Item label="Release date, time">
                            {renderConfiguredDateInfosOfType(LifecycleStep.Release)}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label={'Lifecycle freeze dates'}>
                        {lifecycleFreezes?.length ?
                            displayTextForLifecycleFreezeSets(lifecycleFreezes).map((item, index) => (
                                <span key={index}>
                                    {item}
                                    <br />
                                </span>
                            ))
                        :   <Text type="secondary">No lifecycle freeze dates</Text>}
                    </Descriptions.Item>
                </Descriptions>
            </div>
        </div>
    );
};

export default ReleaseScheduleSettingsComponent;
